import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  ColDefBuilder,
  DataTable,
  Heading,
  Label,
  Stack,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import { AddModelArgsDialog } from './AddModelArgsDialog';
import {
  GlobalBundleBlocker,
  groupGlobalBlockersByFk,
} from './GlobalBundleBlockerTooltip';
import { SetGlobalModelArgsAction } from './SetGlobalModelArgsAction';
import { TemplateBundleDefaultModelsSection } from './TemplateBundleDefaultModelsSection';
import { ToggleModelActiveAction } from './ToggleModelActiveAction';
import { getModelArgsBundleBlockersQuery } from './prompt.operations';

const llmModelArgsFragment = graphql(`
  fragment ModelArgs on LlmModelArgs @_unmask {
    __typename
    id
    modelType
    args
    name
    description
    isActive
    isGlobalDefault
  }
`);

export const allLlmModelArgsFragment = graphql(
  `
    fragment AllLlmModelArgs on query_root @_unmask {
      __typename
      llmModelArgs {
        ...ModelArgs
      }
    }
  `,
  [llmModelArgsFragment],
);

const getModelArgsQuery = graphql(
  `
    query getModels {
      ...AllLlmModelArgs
    }
  `,
  [allLlmModelArgsFragment],
);

type ModelArgsRow = {
  id: string;
  modelType: string;
  name: string | null;
  isGlobalDefault: boolean;
  isActive: boolean;
  description: string | null;
  args: Record<any, any> | null;
  blockers: GlobalBundleBlocker[];
};

const columns = new ColDefBuilder<ModelArgsRow>()
  .defaultSortable('modelType', 'Model')
  .defaultSortable('name')
  .defaultBoolean('isGlobalDefault', 'Default')
  .defaultSortable('args', {
    colOptions: {
      accessorFn: (row) => JSON.stringify(row.args),
    },
    cellRenderer: (row) => <pre>{JSON.stringify(row.args)}</pre>,
  })
  .defaultSortable('description')
  .colDef({
    header: 'Actions',
    cell: ({
      row: {
        original: { id, isGlobalDefault, isActive, blockers },
      },
    }) => (
      <Stack direction="horizontal" gap={2} align="stretch">
        <SetGlobalModelArgsAction
          modelArgsId={id}
          isGlobalDefault={isGlobalDefault}
          isActive={isActive}
        />
        <ToggleModelActiveAction modelArgsId={id} blockers={blockers} />
      </Stack>
    ),
  })
  .build();

const ModelsTabSchema = z
  .enum(['model-args', 'bundle-default-models'])
  .catch('model-args');

type ModelsTab = z.infer<typeof ModelsTabSchema>;

export const ModelsListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ModelsTabSchema.parse(searchParams.get('tab'));

  const setTab = (newTab: ModelsTab) => {
    const params = new URLSearchParams();
    params.set('tab', newTab);
    setSearchParams(params, { replace: true });
  };

  const {
    data: { llmModelArgs: modelArgs },
  } = useSuspenseQuery(getModelArgsQuery);

  const {
    data: { llmModelPromptTemplates: blockerRows },
  } = useSuspenseQuery(getModelArgsBundleBlockersQuery);

  const blockersByModelArgsId = useMemo(
    () => groupGlobalBlockersByFk(blockerRows, (row) => row.modelArgsId),
    [blockerRows],
  );

  const [showInactive, setShowInactive] = useState(false);

  // Each row carries the active bundles (if any) that block deactivating its
  // model args, resolved once per page and grouped by model_args_id above.
  const rows = useMemo<ModelArgsRow[]>(() => {
    const visible = showInactive
      ? modelArgs
      : modelArgs.filter((modelArg) => modelArg.isActive);

    return visible.map((modelArg) => ({
      id: modelArg.id,
      modelType: modelArg.modelType,
      name: modelArg.name,
      isGlobalDefault: modelArg.isGlobalDefault,
      isActive: modelArg.isActive,
      description: modelArg.description,
      args: modelArg.args as Record<any, any> | null,
      blockers: blockersByModelArgsId.get(modelArg.id) ?? [],
    }));
  }, [modelArgs, showInactive, blockersByModelArgsId]);

  return (
    <>
      <div className="my-2 py-2">
        <Heading>Models</Heading>
      </div>
      <Tabs value={tab} className="w-full">
        <TabsList>
          <TabsTrigger
            value="model-args"
            onClick={() => setTab('model-args')}
            className="px-8"
          >
            Model Args
          </TabsTrigger>
          <TabsTrigger
            value="bundle-default-models"
            onClick={() => setTab('bundle-default-models')}
            className="px-8"
          >
            Template Bundle Default Models
          </TabsTrigger>
        </TabsList>

        <TabsContent value="model-args">
          <Stack direction="horizontal" align="center" justify="between">
            <div className="my-2 py-2">
              <Heading size="xl">Model Args</Heading>
            </div>
            <AddModelArgsDialog />
          </Stack>
          <Stack gap={2} direction="horizontal" className="mb-3">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              aria-label="Show Inactive"
            />
            <Label>Show Inactive</Label>
          </Stack>
          <DataTable
            columns={columns}
            data={rows}
            enableGlobalSearch
            initialSortingState={[
              {
                id: 'isGlobalDefault',
                desc: true,
              },
            ]}
          />
        </TabsContent>

        <TabsContent value="bundle-default-models">
          <TemplateBundleDefaultModelsSection />
        </TabsContent>
      </Tabs>
    </>
  );
};
